import { Field, WrappedFieldProps } from 'redux-form';
import { useMemo, useRef } from 'react';
import { createOption, NewTabLink, NumberInput, Select } from '@mews-commander/ui';
import { LocalizedText, LocalizedTextProps, hasInputError, lt, useLocalizeText } from '@mews/framework';
import { CompoundField, CompoundFieldProps } from '@optimus-web/core';
import { SettlementValueType } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { TextKey, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { useFormValueSelector } from '@mews-commander/legacy-form';
import { RateGroupFormSchemaKeys } from '../RateGroupFormSchema';

export interface SettlementValue {
    discriminator: SettlementValueType.Percentage | SettlementValueType.Flat;
    value: {
        value: number | undefined;
    };
}

export interface SettlementValueFieldProps extends Omit<CompoundFieldProps, 'children'> {
    children?: React.ReactNode;
    name: string;
    value: SettlementValue;
    onChange?: (value: SettlementValue) => void;
    selectedCurrency: string;
}

export const SettlementValueInput = ({
    name,
    value: settlementValue,
    onChange,
    selectedCurrency,
    error,
    ...other
}: SettlementValueFieldProps) => {
    const localizeText = useLocalizeText();
    const options = useMemo(
        () => [
            createOption({
                label: localizeText({ textKey: TextKey.SettlementFlatValueLabel }),
                value: SettlementValueType.Flat,
            }),
            createOption({
                label: localizeText({ textKey: TextKey.SettlementPercentageValueLabel }),
                value: SettlementValueType.Percentage,
            }),
        ],
        [localizeText]
    );

    const numberInputRef = useRef<HTMLInputElement>(null);

    const handleTypeChange = (type: SettlementValueType) => {
        const value = type === SettlementValueType.Percentage ? 100 : 0;

        if (numberInputRef.current) {
            numberInputRef.current.value = String(value);
        }

        onChange?.({
            discriminator: type,
            value: {
                value,
            },
        });
    };

    const handleValueChange = (value: number | undefined) => {
        onChange?.({
            ...settlementValue,
            value: { value },
        });
    };

    const valueSuffix =
        settlementValue.discriminator === SettlementValueType.Flat ? selectedCurrency : UnicodeEntity.Percent;

    const helper = (
        <>
            <LocalizedText textKey={TextKey.SettlementValueHint} />
            {UnicodeEntity.NoBreakSpace}
            <NewTabLink url={createGuideLink(ArticlePath.HowToConfigureMultipleSettlementRules)}>
                <LocalizedText textKey={TextKey.LearnMore} />
            </NewTabLink>
        </>
    );

    return (
        <CompoundField name={name} label={lt('SettlementValueLabel')} helper={helper} required {...other}>
            <Select
                name={`${name}.type`}
                options={options}
                selected={settlementValue.discriminator}
                onChange={handleTypeChange}
            />
            <NumberInput
                name={`${name}.value.value`}
                value={settlementValue.value.value}
                onChange={handleValueChange}
                suffix={valueSuffix}
                error={error}
                insideCompoundField
                ref={numberInputRef}
            />
        </CompoundField>
    );
};

const format = (value: SettlementValue) =>
    value.discriminator === SettlementValueType.Percentage && value.value.value
        ? ({
              ...value,
              value: {
                  value: value.value.value * 100,
              },
          } satisfies SettlementValue)
        : value;

const normalize = (value: SettlementValue) =>
    value.discriminator === SettlementValueType.Percentage && value.value.value
        ? ({
              ...value,
              value: {
                  value: value.value.value / 100,
              },
          } satisfies SettlementValue)
        : value;

const SettlementValueFieldRenderer = ({
    /**
     * Redux-form is really dumb, picking up change of values on blur event from child NumberInput component.
     * By leaving out the onBlur event we fix this.
     */
    input: { onBlur, ...otherInput },
    meta,
    ...other
}: SettlementValueFieldProps & WrappedFieldProps) => {
    const selectedCurrency = useFormValueSelector(meta.form, RateGroupFormSchemaKeys.settlementCurrencyCode);

    /**
     * SettlementValueInput is a compound field, but it's registered to the form as a whole,
     * so we need to dissasemble and parse the error here directly.
     *
     * This only works because we know exactly how the field is validated.
     */
    const error = hasInputError(meta) ? <LocalizedText {...(meta.error.value.value as LocalizedTextProps)} /> : null;

    return (
        <SettlementValueInput {...meta} {...otherInput} {...other} error={error} selectedCurrency={selectedCurrency} />
    );
};

export const SettlementValueField = ({ name }: { name: string }) => (
    <Field name={name} component={SettlementValueFieldRenderer} format={format} normalize={normalize} />
);
